<template>
  <div class="image-input">
    <div class="image-input-uploader">
      <div class="image-input__image">
        <img v-if="imageSrc" class="image-input__image" :src="imageSrc">
        <slot v-else name="placeholder"></slot>
      </div>

      <div class="center">

        <!-- Delete and upload buttons -->
        <div class="row justify-center">
          <!-- Upload Button -->
          <div class="col-6 text-center">
            <q-btn
              @click="$refs.fileInput.click()"
              color="white"
              round
              flat
            >
              <q-icon name="cloud_upload" size="2.5rem"/>
            </q-btn>
          </div>

          <!-- Delete Button -->
          <div class="col-6 text-center" v-show="deleteEndpoint && (imageUrl || image)">
            <q-btn
              @click="handleImageDelete"
              color="white"
              round
              flat
            >
              <q-icon name="delete" size="2.5rem"/>
            </q-btn>
          </div>
        </div>
      </div>

      <!-- Hidden file input -->
      <input type="file" ref="fileInput" class="hidden file-input" accept="image/jpeg,image/png" @change="handleImageChanged">

      <!-- Hint -->
      <label class="hint text-center" v-if="showHint">
        <small class="uppercase">{{ $t('image_hint') }}</small>
      </label>
    </div>
  </div>
</template>

<script>
  import { createErrorFromResponse } from 'src/utils/error'
  import imageMixin from 'src/utils/mixins/image'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    props: {
      /**
       * Upload function. Should return a promise
       */
      uploadEndpoint: {
        type: Function,
        required: true
      },

      /**
       * Delete function. Should return a promise
       */
      deleteEndpoint: {
        type: Function
      },

      /**
       * URL to the image
       */
      imageUrl: {
        type: String,
        default: null
      },

      /**
       * Default image if no imageUrl is present
       */
      defaultImage: {
        type: String,
        default: null
      },

      /**
       * Translated image type, e.g. "Avatar"
       */
      imageType: {
        type: String,
        required: true
      },

      /**
       * Maximum file size
       */
      maxSize: {
        type: Number,
        default: 2048
      },

      /**
       * Indicate if the hint should be visible
       */
      showHint: {
        type: Boolean,
        default: false
      }
    },

    data () {
      return {
        image: null
      }
    },

    computed: {
      imageSrc () {
        return this.image ? URL.createObjectURL(this.image)
        : this.imageUrl ? this.getImageUrl(this.imageUrl, 'medium') : this.defaultImage
      }
    },

    created () {
      this.$validator.attach({ name: 'image', rules: `size:${this.maxSize}` })
    },

    methods: {
      handleImageDelete () {
        this.$q.dialog({
          title: this.$t('confirm'),
          message: this.$t('delete_image', { type: this.imageType }),
          ok: this.$t('delete'),
          cancel: this.$t('cancel')
        })
          .then(this.deleteImage)
      },

      handleImageChanged (e) {
        if (e.target.files.length < 1) {
          return
        }

        this.image = e.target.files[0]

        this.$validator.validateAll({
          image: e.target.files
        })
          .then(result => {
            if (!result) {
              this.resetInput()
              this.$q.notify({ message: this.errors.first('image'), type: 'warning' })

              return
            }

            this.storeImage()
          })
      },

      deleteImage () {
        return this.deleteEndpoint()
          .then(() => {
            this.image = null
            this.$q.notify({ message: this.$t('image_deleted', { type: this.imageType }), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      storeImage () {
        this.uploadEndpoint(this.image)
          .then(() => this.$q.notify({ message: this.$t('image_stored', { type: this.imageType }), type: 'positive' }))
          .catch(createErrorFromResponse)
          .then(() => this.resetInput)
      },

      resetInput () {
        this.image = null
        this.$refs.fileInput.value = null
      }
    },

    mixins: [
      imageMixin
    ]
  }
</script>

<style lang="stylus">
  @import '~variables'

  .image-input
    padding: 1rem

    .image-input-uploader
      position: relative
      height: 160px
      width: 160px
      border-radius: 50%
      background: $background-color

      &:hover
        background: black

      &:hover .image-input__image
        opacity: 0.4

      &:hover .center
        opacity: 1
        visibility: visible

      .image-input__image
        width: 100%
        height: 100%
        border-radius 50%
        object-fit: contain
        opacity: 1
        display: block
        transition: .5 ease
        backface-visibility: hidden

      .center
        position: absolute
        width: 75%
        top: 50%
        left: 50%
        transition: .5s ease
        opacity: 0
        visibility: hidden
        transform: translate(-50%, -50%)
        -ms-transform: translate(-50%, -50%)

    .hint
      display: block
      margin-top: 1rem
      color: $faded
</style>
